import {
  extractConvexFunctionName,
  isRedactedConvexServerError,
} from "@shared/telemetry/convex-trace";
import {
  exceptionValue,
  toPosthogExceptionList,
} from "@/lib/error-tracking/posthog-exception-types";

const CONVEX_FUNCTION_KIND_PATTERN = /\[CONVEX ([QMA])\(/;

const CONVEX_FUNCTION_KINDS: Record<string, string> = {
  Q: "query",
  M: "mutation",
  A: "action",
};

interface ConvexServerErrorIssue {
  fingerprint: string;
  name: string;
}

function convexServerErrorIssue(value: string): ConvexServerErrorIssue | undefined {
  if (!isRedactedConvexServerError(value)) return undefined;
  const functionName = extractConvexFunctionName(value);
  if (functionName === undefined) return undefined;
  const kindCode = CONVEX_FUNCTION_KIND_PATTERN.exec(value)?.[1] ?? "";
  const kind = CONVEX_FUNCTION_KINDS[kindCode] ?? "function";
  return {
    fingerprint: `browser-convex-server-error:${functionName}`,
    name: `Convex ${kind} failed: ${functionName}`,
  };
}

export function groupConvexServerErrors<
  TEvent extends { event?: string; properties?: Record<string, unknown> } | null,
>(event: TEvent): TEvent {
  if (event?.event !== "$exception") return event;
  if (event.properties?.$exception_fingerprint !== undefined) return event;
  const [first] = toPosthogExceptionList(event.properties?.$exception_list);
  if (!first) return event;
  const issue = convexServerErrorIssue(exceptionValue(first));
  if (issue === undefined) return event;
  return {
    ...event,
    properties: {
      ...event.properties,
      $exception_fingerprint: issue.fingerprint,
      $issue_name: issue.name,
    },
  };
}
